import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Phone, Clock, Package, ClipboardList, Ruler, Camera, Upload, FileText, Download, ImageIcon, MapPin, ChevronRight, X, Key, User, Lock, CheckCircle2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { getQuote, getMediaUrl, uploadMedia, deleteMedia, updateItem, updateQuote } from "@/lib/api";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/calculations";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import QuoteFamilySelector from "@/components/custom/QuoteFamilySelector";
import MobileMeasurements from "@/components/custom/MobileMeasurements";

const HOURS_PER_DAY = 8;
function formatDuration(h) {
  if (!h || h === 0) return "";
  if (h >= HOURS_PER_DAY) {
    const d = h / HOURS_PER_DAY;
    return `${d % 1 === 0 ? d : d.toFixed(1)} day${d !== 1 ? "s" : ""}`;
  }
  return `${h} hrs`;
}

export default function MobileJob() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [showQuotePrompt, setShowQuotePrompt] = useState(false);
  const [showMapsMenu, setShowMapsMenu] = useState(false);
  const [actionStatus, setActionStatus] = useState({});
  const [actionReasons, setActionReasons] = useState({});

  const [refreshing, setRefreshing] = useState(false);
  // Pull-to-refresh + manual refresh state. ref drives the visual pull indicator,
  // setRefreshing throws up the spinner during the actual fetch.
  const pullRef = useRef({ startY: 0, delta: 0, active: false });
  const [pullDelta, setPullDelta] = useState(0);

  const loadJob = useCallback(async () => {
    try {
      const data = await getQuote(id);
      setJob(data);
      const saved = data.action_completion || {};
      setActionStatus(saved.status || {});
      setActionReasons(saved.reasons || {});
    } catch (err) {
      // 404 = the job genuinely doesn't exist → bounce. Anything else (network, timeout,
      // 5xx) → keep the user on the page, show a clear error, let them tap the refresh
      // icon in the header to retry. Previously we silently navigated away on every error,
      // which made a transient network blip look like "the job vanished" to contractors.
      const status = err?.response?.status;
      if (status === 404) {
        toast.error("Job not found");
        navigate("/mobile/today");
      } else {
        toast.error("Couldn't load this job — check your connection and tap refresh", { duration: 6000 });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id, navigate]);

  const handleManualRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadJob();
  }, [loadJob]);

  const [uploadProgress, setUploadProgress] = useState(null); // null | "compressing" | "uploading" | "done" | "error"

  const compressImage = (file, maxSize = 1600, quality = 0.8) => new Promise((resolve) => {
    if (!file.type.startsWith("image/") || file.type === "image/heic" || file.type === "image/heif") { resolve(file); return; }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width <= maxSize && height <= maxSize && file.size < 500000) { resolve(file); return; }
      if (width > height) { if (width > maxSize) { height = Math.round(height * maxSize / width); width = maxSize; } }
      else { if (height > maxSize) { width = Math.round(width * maxSize / height); height = maxSize; } }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        resolve(blob ? new File([blob], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" }) : file);
      }, "image/jpeg", quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

  useEffect(() => {
    setLoading(true); loadJob();
  }, [loadJob]);

  // Pull-to-refresh — touch-only, activated when the document is scrolled to top.
  // Threshold 70px. While pulling, `pullDelta` drives the rotating indicator at the
  // top of the page. Released past threshold → manual refresh.
  useEffect(() => {
    const onStart = (e) => {
      if (window.scrollY > 4 || refreshing) return;
      pullRef.current.startY = e.touches[0].clientY;
      pullRef.current.active = true;
    };
    const onMove = (e) => {
      if (!pullRef.current.active) return;
      const d = e.touches[0].clientY - pullRef.current.startY;
      if (d > 0 && window.scrollY <= 4) {
        const clamped = Math.min(d, 110);
        pullRef.current.delta = clamped;
        setPullDelta(clamped);
      } else {
        pullRef.current.active = false;
        setPullDelta(0);
      }
    };
    const onEnd = () => {
      if (pullRef.current.active && pullRef.current.delta > 70) {
        handleManualRefresh();
      }
      pullRef.current.active = false;
      pullRef.current.delta = 0;
      setPullDelta(0);
    };
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
    };
  }, [refreshing, handleManualRefresh]);

  const handleUpload = async (file, category) => {
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      await uploadMedia(id, compressed, category);
      toast.success("Uploaded");
      loadJob();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (loading || !job) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}><p className="text-sm" style={{ color: "var(--text-muted)" }}>Loading...</p></div>;
  }

  const photos = (job.media || []).filter(m => m.category === "photo");
  // All photos are displayed, but only photos from current phase count toward completion requirement
  const currentPhasePhotos = job.current_booking_phase
    ? photos.filter(p => p.uploaded_at >= job.current_booking_phase)
    : photos;
  const displayPhotos = photos; // Show ALL photos on the mobile view
  const actions = job.actions || [];
  const mainActions = actions.filter(a => typeof a === "object" && !a.is_sub);
  // For re-bookings: only count actions that are NOT already completed from a previous booking
  const incompleteActions = mainActions.filter((_, idx) => actionStatus[idx + 1] !== true);
  const requiredPhotos = Math.max(incompleteActions.length, 1);
  const receipts = (job.media || []).filter(m => m.category === "receipt");
  const documents = (job.media || []).filter(m => m.category === "document" && !m.is_deleted);
  const items = job.items || [];
  const measurements = job.measurements || [];

  return (
    <div className="min-h-screen pb-6" style={{ background: "var(--bg)" }}>
      {/* Pull-to-refresh indicator (only on touch) */}
      {(pullDelta > 0 || refreshing) && (
        <div
          className="flex items-center justify-center text-xs"
          style={{
            height: Math.max(pullDelta, refreshing ? 48 : 0) + "px",
            color: "var(--text-muted)",
            transition: refreshing ? "height 180ms ease" : "none",
          }}
          data-testid="pull-to-refresh-indicator"
        >
          <RefreshCw
            className="w-4 h-4 mr-1.5"
            style={{
              transform: refreshing ? "rotate(0deg)" : `rotate(${pullDelta * 3}deg)`,
              animation: refreshing ? "spin 0.9s linear infinite" : "none",
            }}
          />
          {refreshing ? "Refreshing…" : pullDelta > 70 ? "Release to refresh" : "Pull to refresh"}
        </div>
      )}
      {/* Header */}
      <div className="bg-white border-b px-4 pt-4 pb-4" style={{ borderColor: "var(--border)" }}>
        <div className="flex items-center justify-between mb-2">
          <button onClick={() => navigate("/mobile/today")} className="flex items-center gap-1 text-sm" style={{ color: "var(--primary)" }}>
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button
            onClick={handleManualRefresh}
            disabled={refreshing}
            className="flex items-center gap-1 text-sm p-1 rounded"
            style={{ color: "var(--text-muted)", opacity: refreshing ? 0.5 : 1 }}
            data-testid="manual-refresh-btn"
            aria-label="Refresh job data"
            title="Reload this job sheet"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-base font-semibold" style={{ color: "var(--text-primary)" }}>
              {job.booking_type === "recall" ? "Recall" : "Contractor Job Sheet"}
            </h1>
            {job.quote_number && <p className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>{job.quote_number}</p>}
          </div>
          <span className="badge-pill badge-blue">{job.client_type}</span>
        </div>
      </div>

      {/* Active Quote selector — same component as desktop, with mobile-aware navigation
          targets so card-tap / Edit / Delete-fallback all stay inside /mobile/* routes. */}
      <div className="px-2 pt-2 pb-1">
        <QuoteFamilySelector
          currentQuoteId={id}
          onFamilyChange={() => loadJob()}
          mobileMode={true}
        />
      </div>

      {/* Inactive-quote warning — surfaces on mobile too. Same colours as desktop banner. */}
      {job && job.selected_for_send === false && (
        <div
          className="mx-3 mb-2 px-3 py-2 rounded-lg flex items-start gap-2"
          style={{ background: "#FFF7ED", border: "1px solid #FDBA74" }}
          data-testid="superseded-banner-mobile"
        >
          <div className="text-base leading-none mt-0.5" style={{ color: "#C2410C" }}>!</div>
          <div className="flex-1">
            <div className="text-xs font-semibold" style={{ color: "#9A3412" }}>Inactive quote</div>
            <div className="text-[11px] mt-0.5" style={{ color: "#9A3412" }}>
              You're viewing an Inactive quote on this job. Use the selector above to switch to
              the Active Quote.
            </div>
          </div>
        </div>
      )}

      <div className="px-4 pt-4 space-y-4" style={job.completed_at ? { pointerEvents: "none", opacity: 0.85 } : undefined}>
        {/* Completed banner */}
        {job.completed_at && (
          <div className="rounded-xl p-3 text-center" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", pointerEvents: "auto" }}>
            <p className="text-sm font-semibold" style={{ color: "#15803D" }}>
              This job was completed on {new Date(job.completed_at).toLocaleDateString("en-GB")} at {new Date(job.completed_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
            </p>
            <p className="text-xs mt-0.5" style={{ color: "#6B7280" }}>No further changes can be made{job.completed_by ? ` · Completed by ${job.completed_by}` : ""}</p>
          </div>
        )}
        {/* Edit Quote button for jobs awaiting quote */}
        {(job.status === "Awaiting Quote" || (job.status === "Not Started" && job.quote_started)) && !job.completed_at && (
          <button onClick={() => navigate(`/mobile/quote/${id}`)} className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2" style={{ background: "#E05C2A", color: "#fff", borderRadius: "12px", pointerEvents: "auto" }}>
            <FileText className="w-4 h-4" /> Edit Quote
          </button>
        )}
        {/* Lead Contractor indicator */}
        {(() => {
          const memberId = localStorage.getItem("team_member_id");
          const isLead = !job.lead_contractor_id || job.lead_contractor_id === memberId;
          const additionalMembers = job.additional_team_members || [];
          const isAdditional = additionalMembers.some(am => am.team_member_id === memberId);
          const allMembers = [job.booked_team_member_name, ...additionalMembers.map(am => am.name)].filter(Boolean);

          if (allMembers.length > 1) {
            return (
              <div className="rounded-xl p-3" style={{ background: isLead ? "var(--primary-light)" : "var(--bg)", border: `2px solid ${isLead ? "var(--primary)" : "var(--border)"}` }}>
                {isLead ? (
                  <>
                    <p className="text-xs font-bold uppercase" style={{ color: "var(--primary)" }}>You are the Lead Contractor</p>
                    <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>You must complete the job pack. Team: {allMembers.join(", ")}</p>
                  </>
                ) : (
                  <>
                    <p className="text-xs font-bold uppercase" style={{ color: "var(--text-secondary)" }}>Supporting Role</p>
                    <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>Lead: {job.booked_team_member_name}. No job pack completion required from you.</p>
                  </>
                )}
              </div>
            );
          }
          return null;
        })()}

        {/* Job Info */}
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: "var(--border)" }}>
          <div className="space-y-3">
            {job.booking_type === "recall" && (
              <div className="rounded-xl p-3 text-center" style={{ background: "var(--warning-light)", border: "2px solid var(--warning)" }}>
                <span className="text-xs font-bold uppercase" style={{ color: "#92400E" }}>Recall Visit</span>
                {job.recall_reason && (
                  <p className="text-sm font-medium mt-2" style={{ color: "#92400E" }}>{job.recall_reason}</p>
                )}
              </div>
            )}
            <div>
              <p className="ui-label">Address</p>
              <div className="flex items-center gap-2 mt-0.5">
                <p style={{ fontSize: "16px", fontWeight: 700, color: "var(--text-primary)", flex: 1 }}>{job.site_address || "—"}</p>
                {job.site_address && (
                  <button
                    onClick={() => setShowMapsMenu(true)}
                    className="flex items-center gap-1.5 flex-shrink-0 active:scale-95 transition-transform"
                    style={{ background: "#16a34a", color: "#fff", padding: "8px 16px", borderRadius: "6px" }}
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    <span className="text-xs font-semibold">Open in Maps</span>
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="ui-label">Client</p>
                <p className="text-sm font-medium mt-0.5">{job.client_type}</p>
              </div>
              <div>
                <p className="ui-label">Date</p>
                <p className="text-sm font-medium mt-0.5">{job.start_date || "—"}</p>
              </div>
              {job.booked_start_time && (
                <div>
                  <p className="ui-label">Time</p>
                  <p className="text-sm font-medium mt-0.5 flex items-center gap-1"><Clock className="w-3 h-3" style={{ color: "var(--text-muted)" }} />{job.booked_start_time} — {job.booked_end_time}</p>
                </div>
              )}
              <div>
                <p className="ui-label">Assigned To</p>
                <p className="text-sm font-medium mt-0.5">{job.booked_team_member_name || "—"}</p>
              </div>
            </div>
            {job.tenant_phone && (
              <a href={`tel:${job.tenant_phone.replace(/\s/g, "")}`} className="flex items-center justify-center gap-2 mt-3 w-full rounded-lg font-semibold transition-colors active:opacity-80" style={{ background: "#dcfce7", color: "#16a34a", padding: "12px 16px", fontSize: "14px" }}>
                <Phone className="w-5 h-5" /> Call Tenant: {job.tenant_phone}
              </a>
            )}
            {job.booking_type === "quote" && job.correspondence_phone && (
              <a href={`tel:${job.correspondence_phone.replace(/\s/g, "")}`} className="flex items-center gap-2 mt-1 p-3 rounded-xl font-medium text-sm transition-colors active:opacity-80" style={{ background: "var(--primary-light)", color: "var(--primary)" }}>
                <Phone className="w-4 h-4" /> Call Correspondence: {job.correspondence_phone}
              </a>
            )}
          </div>
        </div>

        {/* Job Description (quote appointments only) */}
        {job.booking_type === "quote" && job.job_details && (
          <div className="bg-white rounded-xl border p-4" style={{ borderColor: "var(--border)" }}>
            <h3 className="ui-label mb-2 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Job Description</h3>
            <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-primary)" }}>{job.job_details}</p>
          </div>
        )}

        {/* Quote Visit Notes (quote appointments only) */}
        {job.booking_type === "quote" && (
          <div className="bg-white rounded-xl border p-4" style={{ borderColor: "var(--border)" }}>
            <h3 className="ui-label mb-2 flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5" /> Quote Visit Notes</h3>
            <textarea
              value={job.quote_visit_notes || ""}
              onChange={(e) => setJob(prev => ({ ...prev, quote_visit_notes: e.target.value }))}
              onBlur={(e) => { updateQuote(id, { quote_visit_notes: e.target.value }).catch(() => toast.error("Failed to save notes")); }}
              placeholder="Enter notes from the quote visit..."
              rows={4}
              className="w-full rounded-xl p-3 text-sm resize-y focus:outline-none transition-colors"
              style={{ border: "1px solid var(--border)", color: "var(--text-primary)" }}
              data-testid="quote-visit-notes"
            />
          </div>
        )}

        {/* Parking */}
        {job.parking_type && (
          <div className="bg-white rounded-xl border p-4" style={{ borderColor: "var(--border)" }}>
            <h3 className="ui-label mb-2">Parking</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-[10px] uppercase" style={{ color: "var(--text-muted)" }}>Type</p><p className="font-medium">{job.parking_type}</p></div>
              {job.parking_hours > 0 && <div><p className="text-[10px] uppercase" style={{ color: "var(--text-muted)" }}>Hours</p><p className="font-medium">{job.parking_hours}</p></div>}
            </div>
          </div>
        )}

        {/* Access */}
        {job.access_method && (
          <div className="bg-white rounded-xl border p-4" style={{ borderColor: "var(--border)", borderLeft: "4px solid #2680C2" }}>
            <h3 className="ui-label mb-2" style={{ color: "#2680C2" }}>Access</h3>
            {job.access_method === "keys_branch" && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Key className="w-4 h-4" style={{ color: "#2680C2" }} />
                  <span className="text-sm font-semibold">Keys: Collect from Branch</span>
                </div>
                {job.access_branch_address && <p className="text-sm font-medium ml-6">{job.access_branch_address}</p>}
                <p className="text-xs ml-6" style={{ color: "var(--text-muted)" }}>Collect keys before attending the property.</p>
              </div>
            )}
            {job.access_method === "occupier" && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" style={{ color: "#2680C2" }} />
                  <span className="text-sm font-semibold">Access via Occupier</span>
                </div>
                {(job.tenant_phone || job.access_occupier_phone) && (
                  <a href={`tel:${(job.tenant_phone || job.access_occupier_phone).replace(/\s/g, "")}`} className="flex items-center gap-2 ml-6 text-sm font-medium" style={{ color: "#16a34a" }}>
                    <Phone className="w-3.5 h-3.5" /> {job.tenant_phone || job.access_occupier_phone}
                  </a>
                )}
                <p className="text-xs ml-6" style={{ color: "var(--text-muted)" }}>Tenant or occupier will provide access on arrival.</p>
              </div>
            )}
            {job.access_method === "other" && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" style={{ color: "#2680C2" }} />
                  <span className="text-sm font-semibold">Access Instructions</span>
                </div>
                {job.access_notes && <p className="text-sm ml-6 whitespace-pre-wrap">{job.access_notes}</p>}
              </div>
            )}
          </div>
        )}

        {/* Actions — tick off completed */}
        {actions.length > 0 && (
          <div className="rounded-xl p-4" style={{ background: "#F0FDF4", border: "2px solid #22C55E" }}>
            <h3 className="flex items-center gap-1.5 text-sm font-bold mb-3" style={{ color: "#15803D" }}>
              <ClipboardList className="w-4 h-4" /> Actions ({mainActions.length}) — {Object.values(actionStatus).filter(Boolean).length}/{job.booking_type === "recall" && job.recall_actions ? job.recall_actions.length : mainActions.length} complete
            </h3>
            {(() => {
              const recallNums = job.recall_actions || [];
              const isRecall = job.booking_type === "recall" && recallNums.length > 0;
              // Pre-compute label + actionNum for every entry so we can group without losing numbering
              let mn = 0, sn = 0;
              const annotated = actions.map(a => {
                const act = typeof a === "string" ? { description: a, is_sub: false } : a;
                if (!act.is_sub) { mn++; sn = 0; } else { sn++; }
                return { act, label: act.is_sub ? `${mn}.${sn}` : `${mn}`, actionNum: mn, isMain: !act.is_sub };
              });

              const renderRow = ({ act, label, actionNum, isMain }, interactive) => {
                const state = actionStatus[actionNum];
                const isSet = state !== undefined;
                const isDone = state === true;
                const isLockedDone = !interactive && isDone;
                return (
                  <div key={`${actionNum}-${label}`} className={isMain ? "" : "ml-12"} style={{ paddingTop: 8, paddingBottom: 8 }}>
                    <div className="flex gap-3 items-start">
                      <span className={`shrink-0 w-7 h-7 inline-flex items-center justify-center rounded-full text-[11px] font-bold ${isDone ? "bg-emerald-100 text-emerald-700" : isSet && !isDone ? "bg-red-100 text-red-700" : "bg-white text-slate-500 border border-slate-300"}`}>{label}</span>
                      <div className="flex-1 text-sm">
                        <p style={{ color: "var(--text-primary)" }}>{act.description}</p>
                      </div>
                      {interactive ? (
                        <div className="flex gap-1.5 shrink-0" data-testid={`action-toggle-${actionNum}`}>
                          <button type="button" onClick={() => { const next = { ...actionStatus, [actionNum]: true }; setActionStatus(next); updateQuote(id, { action_completion: { status: next, reasons: actionReasons } }).catch(() => {}); }} className={`px-2.5 py-1 rounded-md text-[11px] font-semibold ${isDone ? "bg-emerald-500 text-white" : "bg-white border border-slate-300 text-slate-600"}`}>Done</button>
                          <button type="button" onClick={() => { const next = { ...actionStatus, [actionNum]: false }; setActionStatus(next); updateQuote(id, { action_completion: { status: next, reasons: actionReasons } }).catch(() => {}); }} className={`px-2.5 py-1 rounded-md text-[11px] font-semibold ${isSet && !isDone ? "bg-red-500 text-white" : "bg-white border border-slate-300 text-slate-600"}`}>No</button>
                        </div>
                      ) : (
                        <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: isLockedDone ? "#D1FAE5" : "#E5E7EB", color: isLockedDone ? "#065F46" : "#475569" }} data-testid={`action-locked-${actionNum}`}>
                          <Lock className="w-2.5 h-2.5" />
                          {isLockedDone ? <CheckCircle2 className="w-2.5 h-2.5" /> : null}
                          {isLockedDone ? "Completed previous visit" : "Previous visit"}
                        </span>
                      )}
                    </div>
                    {interactive && isMain && isSet && !isDone && (
                      <div className="mt-2 ml-10">
                        <textarea
                          value={actionReasons[actionNum] || ""}
                          onChange={(e) => setActionReasons(p => ({ ...p, [actionNum]: e.target.value }))}
                          onBlur={() => updateQuote(id, { action_completion: { status: actionStatus, reasons: actionReasons } }).catch(() => {})}
                          placeholder="Describe what prevented completion..."
                          rows={2}
                          className="w-full text-xs rounded-lg p-2 resize-y"
                          style={{ border: "1px solid #FCA5A5", background: "#FEF2F2", color: "#B91C1C" }}
                        />
                      </div>
                    )}
                  </div>
                );
              };

              if (!isRecall) {
                return annotated.map(row => renderRow(row, true));
              }
              // Recall: split into previously-completed (locked) + outstanding (interactive)
              const lockedRows = annotated.filter(r => !recallNums.includes(r.actionNum));
              const activeRows = annotated.filter(r => recallNums.includes(r.actionNum));
              return (
                <>
                  {lockedRows.length > 0 && (
                    <div className="mb-3 pb-3" style={{ borderBottom: "1px dashed #BBF7D0" }} data-testid="recall-locked-section">
                      <p className="text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: "#65A30D" }}>Completed on previous visit — no action required</p>
                      {lockedRows.map(row => renderRow(row, false))}
                    </div>
                  )}
                  {activeRows.length > 0 && (
                    <div data-testid="recall-active-section">
                      <p className="text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: "#B45309" }}>Actions required on this visit</p>
                      {activeRows.map(row => renderRow(row, true))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Materials */}
        {items.length > 0 && (
          <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: "var(--border)" }}>
            <div className="px-4 py-3 flex items-center gap-2" style={{ background: "var(--primary)", borderBottom: "1px solid var(--border)" }}>
              <Package className="w-4 h-4 text-white" />
              <h3 className="text-sm font-bold text-white uppercase tracking-wide">Materials ({items.length})</h3>
            </div>
            {items.map((item, idx) => (
              <div key={item.id} className="p-4" style={{ borderBottom: idx < items.length - 1 ? "1px solid var(--border)" : "none" }}>
                {/* Item name */}
                <p className="text-base font-semibold mb-2" style={{ color: "var(--text-primary)" }}>{item.item_name || "Unnamed item"}</p>

                {/* Collection details — prominent */}
                <div className="rounded-lg p-3 mb-2" style={{ background: "var(--primary-light)", border: "1px solid var(--primary)" }}>
                  <p className="text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: "var(--primary)" }}>Collection Details</p>
                  <div className="grid grid-cols-2 gap-y-1.5">
                    <div>
                      <p className="text-[10px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Supplier</p>
                      <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{item.supplier || "—"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Collect From</p>
                      <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{item.location || "—"}</p>
                    </div>
                    {item.branch_location && (
                      <div className="col-span-2">
                        <p className="text-[10px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Branch</p>
                        <p className="text-sm font-semibold" style={{ color: "var(--primary)" }}>{item.branch_location}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-[10px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Quantity</p>
                      <p className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>{item.qty}</p>
                    </div>
                    {item.sku && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>SKU</p>
                        <p className="text-sm font-mono" style={{ color: "var(--text-secondary)" }}>{item.sku}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Order number — big and obvious */}
                {item.order_number && (
                  <div className="rounded-lg p-3 mb-2" style={{ background: "var(--warning-light)", border: "1px solid var(--warning)" }}>
                    <p className="text-[10px] font-bold uppercase tracking-wide" style={{ color: "#92400E" }}>Order / Collection Code</p>
                    <p className="text-lg font-mono font-bold mt-0.5" style={{ color: "#92400E" }}>{item.order_number}</p>
                  </div>
                )}

                {/* Status badge */}
                <span className={`badge-pill ${item.ordered ? "badge-success" : "badge-danger"}`} style={{ fontSize: "12px", padding: "4px 12px" }}>
                  {item.ordered ? "Ordered" : "Not ordered"}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Measurements — editable on quote visits (booking_type === "quote" OR
            still-untriaged "Quote Booked" status, per Lucas's spec point 2).
            Read-only stack on execution/recall visits. */}
        {(job.booking_type === "quote" || job.status === "Quote Booked") ? (
          <MobileMeasurements
            quoteId={id}
            initial={measurements}
            onSyncedExternal={() => { /* parent already has fresh data via loadJob hooks */ }}
          />
        ) : (
          measurements.length > 0 && (
            <div className="bg-white rounded-xl border p-4" style={{ borderColor: "var(--border)" }}>
              <h3 className="ui-label mb-3 flex items-center gap-1.5"><Ruler className="w-3.5 h-3.5" /> Measurements</h3>
              {measurements.map((m, i) => (
                <div key={m._uid || i} className="flex items-center gap-3 py-2 border-b last:border-0 text-sm" style={{ borderColor: "var(--border)" }}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{m.label || "—"}</div>
                    {m.notes && <div className="text-[11px] italic mt-0.5" style={{ color: "var(--text-muted)" }}>{m.notes}</div>}
                  </div>
                  <span className="font-mono">{m.value}</span>
                  <span className="ui-label">{m.unit}</span>
                </div>
              ))}
            </div>
          )
        )}

        {/* Work Report (execution/recall only) */}
        {job.booking_type !== "quote" && job.status !== "Quote Booked" && (
          <div className="bg-white rounded-xl border p-4" style={{ borderColor: "var(--border)" }}>
            <h3 className="ui-label mb-2 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Work Report</h3>
          <textarea
            value={job.work_report || ""}
            onChange={(e) => setJob(prev => ({ ...prev, work_report: e.target.value }))}
            onBlur={(e) => { updateQuote(id, { work_report: e.target.value }).catch(() => toast.error("Failed to save")); }}
            placeholder="Describe work carried out, any issues found, follow-up needed..."
            rows={5}
            className="w-full rounded-xl p-3 text-sm resize-y focus:outline-none transition-colors"
            style={{ border: "1px solid var(--border)", color: "var(--text-primary)" }}
            data-testid="work-report"
          />
          </div>
        )}

        {/* Job Photos */}
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: currentPhasePhotos.length < requiredPhotos ? "var(--danger)" : "var(--border)" }}>
          <h3 className="ui-label mb-1 flex items-center gap-1.5">
            <Camera className="w-3.5 h-3.5" /> Job Photos ({displayPhotos.length})
            {currentPhasePhotos.length < requiredPhotos && (
              <span className="badge-pill badge-danger ml-1">{requiredPhotos - currentPhasePhotos.length} more needed this visit</span>
            )}
          </h3>
          <p className="text-[11px] mb-3" style={{ color: "var(--text-muted)" }}>Appear on the quote and invoice PDF.</p>
          {displayPhotos.length > 0 && (
            <div className="space-y-1.5 mb-3">
              {displayPhotos.map(p => (
                <div key={p.id} className="flex items-center gap-2.5 py-2 px-3 rounded-lg border" style={{ borderColor: "var(--border)" }}>
                  <button onClick={() => setEnlargedPhoto(p)} className="flex items-center gap-2.5 flex-1 min-w-0 text-left">
                    <ImageIcon className="w-4 h-4 flex-shrink-0" style={{ color: "var(--text-muted)" }} />
                    <span className="text-sm truncate flex-1" style={{ color: "var(--text-primary)" }}>{p.original_filename}</span>
                  </button>
                  <button onClick={async () => { if (!window.confirm("Delete this photo?")) return; try { await deleteMedia(id, p.id); toast.success("Deleted"); loadQuote(); } catch (err) { console.error(err); toast.error("Failed"); } }} className="p-1.5 rounded-lg flex-shrink-0" style={{ color: "var(--danger)" }}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <label className={`flex items-center justify-center gap-2 py-3 border-2 border-dashed rounded-xl cursor-pointer transition-colors active:bg-gray-50 ${uploading ? "opacity-50 pointer-events-none" : ""}`} style={{ borderColor: uploading ? "var(--primary)" : "var(--border)" }}>
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" style={{ color: "var(--primary)" }} />
                <span className="text-sm font-medium" style={{ color: "var(--primary)" }}>Uploading...</span>
              </>
            ) : (
              <>
                <Camera className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
                <span className="text-sm" style={{ color: "var(--text-secondary)" }}>Take or upload photo</span>
              </>
            )}
            <input type="file" accept="image/*,.pdf,.heic,.heif" multiple className="hidden" disabled={uploading} onChange={async (e) => {
              const files = Array.from(e.target.files || []).slice(0, 20);
              if (files.length === 0) return;
              setUploading(true);
              let done = 0;
              for (const f of files) {
                try {
                  const compressed = await compressImage(f);
                  await uploadMedia(id, compressed, "photo");
                  done++;
                } catch (err) { console.error(err); }
              }
              toast.success(`${done} photo${done > 1 ? "s" : ""} uploaded`);
              loadJob();
              setUploading(false);
              e.target.value = "";
            }} />
          </label>
        </div>

        {/* Material Purchases — separate, not in PDFs */}
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: "var(--border)" }}>
          <h3 className="ui-label mb-1 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Material Purchases ({receipts.length})</h3>
          <p className="text-[11px] mb-3" style={{ color: "var(--text-muted)" }}>For your records only — not on PDFs.</p>
          {receipts.length > 0 && (
            <div className="space-y-2 mb-3">
              {receipts.map(r => (
                <div key={r.id} className="flex items-center gap-2 py-2 px-3 rounded-lg border" style={{ background: "var(--bg)", borderColor: "var(--border)" }}>
                  <FileText className="w-4 h-4 flex-shrink-0" style={{ color: "var(--text-muted)" }} />
                  <a href={getMediaUrl(r.storage_path)} target="_blank" rel="noopener noreferrer" className="text-sm flex-1 truncate" style={{ color: "var(--primary)" }}>{r.original_filename}</a>
                  <a href={getMediaUrl(r.storage_path)} download={r.original_filename} target="_blank" rel="noopener noreferrer" style={{ color: "var(--text-muted)" }} className="p-1">
                    <Download className="w-3.5 h-3.5" />
                  </a>
                </div>
              ))}
            </div>
          )}
          <label className={`flex items-center justify-center gap-2 py-3 border-2 border-dashed rounded-xl cursor-pointer transition-colors active:bg-gray-50 ${uploading ? "opacity-50 pointer-events-none" : ""}`} style={{ borderColor: "var(--border)" }}>
            <Upload className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
            <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{uploading ? "Uploading..." : "Upload receipt or invoice"}</span>
            <input type="file" accept="image/*,.pdf" multiple className="hidden" onChange={async (e) => {
              const files = Array.from(e.target.files || []).slice(0, 20);
              if (files.length === 0) return;
              setUploading(true);
              for (const f of files) { try { await uploadMedia(id, f, "receipt"); } catch (err) { console.error(err); } }
              toast.success(`${files.length} file${files.length > 1 ? "s" : ""} uploaded`);
              loadJob();
              setUploading(false);
              e.target.value = "";
            }} />
          </label>
        </div>

        {/* Documents — internal reference only (read-only for contractors) */}
        {documents.length > 0 && (
          <div className="bg-white rounded-xl border p-4" style={{ borderColor: "var(--border)" }} data-testid="mobile-documents">
            <h3 className="ui-label mb-1 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Documents ({documents.length})</h3>
            <p className="text-[11px] mb-3" style={{ color: "var(--text-muted)" }}>Internal reference only — not on PDFs.</p>
            <div className="space-y-2">
              {documents.map(d => (
                <div key={d.id} className="flex items-center gap-2 py-2 px-3 rounded-lg border" style={{ background: "var(--bg)", borderColor: "var(--border)" }}>
                  <FileText className="w-4 h-4 flex-shrink-0" style={{ color: "var(--text-muted)" }} />
                  <a href={getMediaUrl(d.storage_path)} target="_blank" rel="noopener noreferrer" className="text-sm flex-1 truncate" style={{ color: "var(--primary)" }}>{d.original_filename}</a>
                  <a href={getMediaUrl(d.storage_path)} download={d.original_filename} target="_blank" rel="noopener noreferrer" style={{ color: "var(--text-muted)" }} className="p-1">
                    <Download className="w-3.5 h-3.5" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Complete Button */}
        {(() => {
          const memberId = localStorage.getItem("team_member_id");
          const isLead = !job.lead_contractor_id || job.lead_contractor_id === memberId;
          const hasAdditional = (job.additional_team_members || []).length > 0;
          const isCompleted = !!job.completed_at;

          // Already completed — show read-only banner
          if (isCompleted) {
            return (
              <div className="pt-2 pb-4 space-y-2">
                <div className="w-full py-2 px-4 rounded-xl text-center text-xs" style={{ background: "#F0FDF4", color: "#15803D", border: "1px solid #BBF7D0" }}>
                  This job was completed on {new Date(job.completed_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} at {new Date(job.completed_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}. No further changes can be made.
                </div>
                <div className="w-full py-4 rounded-xl font-bold text-base text-center flex items-center justify-center gap-2" style={{ background: "#F0FDF4", color: "#15803D" }}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  {job.completion_type === "partial" ? "Partially Completed" : job.booking_type === "quote" ? "Quote Visit Completed" : job.booking_type === "recall" ? "Recall Completed" : "Job Pack Completed"}
                </div>
              </div>
            );
          }

          // Non-lead on multi-person job: no complete button
          if (hasAdditional && !isLead) {
            return (
              <div className="pt-2 pb-4">
                <div className="w-full py-4 rounded-xl font-medium text-sm text-center" style={{ background: "var(--bg)", color: "var(--text-muted)", border: "1px solid var(--border)" }}>
                  Job pack completion is managed by the Lead Contractor ({job.booked_team_member_name})
                </div>
              </div>
            );
          }

          const PROTECTED_BILLING_STATUSES = ["Invoiced", "Paid", "Partially Refunded", "Refunded"];
          const isProtectedBilling = PROTECTED_BILLING_STATUSES.includes(job.status);
          // Hide the Complete button entirely when the job has already reached a
          // terminal billing status — the recall workflow is the only legitimate
          // path to re-open such a job, and that goes through its own UI.
          return (job.status !== "Job Signed" && job.status !== "Awaiting Quote" && !isProtectedBilling && !(job.booking_type === "recall" && (job.status === "Invoiced" || job.status === "Job Signed"))) ? (
          <div className="pt-2 pb-4 space-y-2">
            {currentPhasePhotos.length < requiredPhotos && (
              <div className="text-center py-2 px-3 rounded-xl text-sm font-medium" style={{ background: "var(--danger-light)", color: "#B91C1C" }}>
                Upload {requiredPhotos - currentPhasePhotos.length} more photo(s) — 1 required per action ({currentPhasePhotos.length}/{requiredPhotos})
              </div>
            )}
            <button
              onClick={async () => {
                const recallNums = job.recall_actions || [];
                const isRecall = job.booking_type === "recall" && recallNums.length > 0;
                const actionsToCheck = isRecall ? mainActions.filter((_, idx) => recallNums.includes(idx + 1)) : mainActions;
                const requiredCount = actionsToCheck.length;

                if (currentPhasePhotos.length < requiredCount) { toast.error(`Need ${requiredCount} photos (1 per action). Currently ${currentPhasePhotos.length}.`); return; }
                const uncheckedWithoutReason = actionsToCheck.filter((_, idx) => {
                  const num = isRecall ? recallNums[idx] : idx + 1;
                  return actionStatus[num] !== true && !actionReasons[num]?.trim();
                });
                if (uncheckedWithoutReason.length > 0) { toast.error("Please provide a reason for incomplete actions"); return; }
                const unsetActions = actionsToCheck.filter((_, idx) => {
                  const num = isRecall ? recallNums[idx] : idx + 1;
                  return actionStatus[num] === undefined;
                });
                if (unsetActions.length > 0) { toast.error("Please mark all actions as Done or No"); return; }

                const contractorName = localStorage.getItem("team_member_name") || "Contractor";
                await updateQuote(id, { action_completion: { status: actionStatus, reasons: actionReasons } });
                setCompleting(true);
                try {
                  // Protected billing statuses are a one-way gate. Never request a
                  // reversal of status from the job-sheet completion. The work itself
                  // is still saved above; only the status update is suppressed.
                  const PROTECTED = ["Invoiced", "Paid", "Partially Refunded", "Refunded"];
                  const isProtected = PROTECTED.includes(job.status);
                  let newStatus;
                  if (isProtected) newStatus = null;
                  else if (job.booking_type === "quote") newStatus = "Awaiting Quote";
                  else if (job.booking_type === "recall") newStatus = job.pre_recall_status || "Job Signed";
                  else newStatus = "Job Signed";
                  if (newStatus) {
                    await api.put(`/quotes/${id}?changed_by=${encodeURIComponent(contractorName)}`, { status: newStatus });
                  }
                  if (job.booking_type === "quote") {
                    toast.success("Quote visit completed");
                    setShowQuotePrompt(true);
                  } else {
                    toast.success(job.booking_type === "recall" ? "Recall completed" : "Job pack completed");
                    navigate("/mobile/today");
                  }
                } catch (err) {
                  const msg = err?.response?.data?.detail || "You must upload at least 1 photo to complete this job";
                  toast.error(msg);
                }
                finally { setCompleting(false); }
              }}
              disabled={completing || currentPhasePhotos.length < requiredPhotos}
              className="w-full py-4 rounded-xl text-white font-bold text-base transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: currentPhasePhotos.length < requiredPhotos ? "#9CA3AF" : "var(--primary)" }}
              data-testid="complete-job-pack"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              {completing ? "Completing..." : job.booking_type === "quote" ? "Complete Quote Visit" : "Complete Job Pack"}
            </button>
          </div>
        ) : (
          <div className="pt-2 pb-4">
            <div className="w-full py-4 rounded-xl font-bold text-base text-center flex items-center justify-center gap-2" style={{ background: "var(--success-light)", color: "#15803D" }}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              {isProtectedBilling
                ? `${job.status} — Job Pack Completed`
                : job.booking_type === "quote" ? "Quote Visit Completed"
                : job.booking_type === "recall" ? "Recall Completed"
                : "Job Pack Completed"}
            </div>
          </div>
        );
        })()}
      </div>

      {/* Maps menu */}
      <Dialog open={showMapsMenu} onOpenChange={setShowMapsMenu}>
        <DialogContent className="[85vw] p-4">
          <p className="text-sm font-semibold mb-3" style={{ color: "var(--text-primary)" }}>Open in Maps</p>
          <div className="space-y-2">
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job?.site_address || "")}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setShowMapsMenu(false)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors active:bg-slate-50"
              style={{ border: "1px solid var(--border)" }}
            >
              <span className="text-lg">🗺️</span>
              <span className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>Google Maps</span>
            </a>
            <a
              href={`https://maps.apple.com/?q=${encodeURIComponent(job?.site_address || "")}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setShowMapsMenu(false)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors active:bg-slate-50"
              style={{ border: "1px solid var(--border)" }}
            >
              <span className="text-lg">🍎</span>
              <span className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>Apple Maps</span>
            </a>
            <a
              href={`https://waze.com/ul?q=${encodeURIComponent(job?.site_address || "")}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setShowMapsMenu(false)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors active:bg-slate-50"
              style={{ border: "1px solid var(--border)" }}
            >
              <span className="text-lg">🚗</span>
              <span className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>Waze</span>
            </a>
          </div>
        </DialogContent>
      </Dialog>

      {/* Enlarged photo with navigation */}
      <Dialog open={!!enlargedPhoto} onOpenChange={() => setEnlargedPhoto(null)}>
        <DialogContent className="[95vw] p-1">
          {enlargedPhoto && (() => {
            const idx = currentPhasePhotos.findIndex(p => p.id === enlargedPhoto.id);
            const hasPrev = idx > 0;
            const hasNext = idx < currentPhasePhotos.length - 1;
            return (
              <div className="relative">
                <img src={getMediaUrl(enlargedPhoto.storage_path)} alt={enlargedPhoto.original_filename} className="w-full h-auto max-h-[80vh] object-contain rounded-lg" />
                {hasPrev && (
                  <button
                    onClick={() => setEnlargedPhoto(currentPhasePhotos[idx - 1])}
                    className="absolute left-1 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center bg-black/40 text-white active:bg-black/60"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                  </button>
                )}
                {hasNext && (
                  <button
                    onClick={() => setEnlargedPhoto(currentPhasePhotos[idx + 1])}
                    className="absolute right-1 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center bg-black/40 text-white active:bg-black/60"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                  </button>
                )}
                <p className="text-center text-xs mt-2" style={{ color: "var(--text-muted)" }}>{idx + 1} / {currentPhasePhotos.length}</p>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Quote creation prompt after completing a quote visit */}
      <Dialog open={showQuotePrompt} onOpenChange={() => {}}>
        <DialogContent style={{ background: "#ffffff", borderRadius: "16px", padding: "28px", maxWidth: "calc(100vw - 32px)" }}>
          <DialogHeader><DialogTitle>Quote ready to create</DialogTitle></DialogHeader>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Would you like to create the quote for <strong>{job?.site_address?.split(",")[0]}</strong> now on your phone, or leave it to be done later on desktop?</p>
          <div className="space-y-2 pt-2">
            <Button onClick={() => { setShowQuotePrompt(false); navigate(`/mobile/quote/${id}`); }} className="w-full h-12 text-base font-semibold" style={{ background: "#E05C2A", color: "#fff", borderRadius: "12px" }}>Create Quote Now</Button>
            <Button variant="outline" onClick={() => { setShowQuotePrompt(false); navigate("/mobile/today"); }} className="w-full h-12 text-base" style={{ borderRadius: "12px" }}>Do it Later on Desktop</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
